// Which signup journey is being rendered.
//
// The client ID is the only thing that distinguishes the flows — see
// ARCHITECTURE.md "Signup Flows (A / B / C)". Auth0 renders the SAME ACUL
// bundle for every client, so the branch has to happen at runtime here rather
// than by serving different assets:
//
//   * Rendering filters cannot do it. There is one rendering configuration per
//     prompt+screen, and requests that don't match a filter fall back to
//     STANDARD rendering, not to a second ACUL bundle.
//   * Liquid `{{ client.id }}` segments in head_tags URLs would work, but need
//     per-client asset directories named after a client ID that Terraform
//     mints — fragile across rebuilds.
//
// `ClientMembers.id` is a required string on every ACUL screen's client model
// (verified in the installed SDK's own types), so this is always available.
export type SignupFlow = "A" | "B";

// Fed in at build time from the Terraform output, never hardcoded — same rule
// as the Action secrets. Unset (e.g. a local build before the client exists)
// degrades to Flow A, which is the safe default: it is the fully-featured
// journey, so a misdetection collects too much rather than too little.
//
// Injected via Vite's `define` (see vite.config.ts) rather than read from
// `import.meta.env` directly: `import.meta` is a hard syntax error under
// Jest's CJS transform, which took out three test suites when this module was
// first written that way. A plain replaced identifier works in both, with
// jest.config.cjs supplying an empty default.
declare const __MEMBER_PORTAL_V2_CLIENT_ID__: string;

const V2_CLIENT_ID: string | undefined =
  typeof __MEMBER_PORTAL_V2_CLIENT_ID__ === "string" &&
  __MEMBER_PORTAL_V2_CLIENT_ID__.length > 0
    ? __MEMBER_PORTAL_V2_CLIENT_ID__
    : undefined;

export function detectFlow(clientId: string | undefined | null): SignupFlow {
  if (!V2_CLIENT_ID || !clientId) return "A";
  return clientId === V2_CLIENT_ID ? "B" : "A";
}

// Stepper labels per flow. Flow B defers DOB, gender, title, country, address
// and TFN to progressive profiling, so it has no "Check details" review step —
// there is too little to review — and its step 3 collects only the identity
// essentials.
const JOURNEY_STEPS: Record<SignupFlow, readonly string[]> = {
  A: [
    "Let's start",
    "Confirm your email",
    "Personal info",
    "Set password",
    "Check details",
  ],
  B: ["Let's start", "Confirm your email", "Your details", "Set password"],
};

export function journeySteps(flow: SignupFlow): readonly string[] {
  return JOURNEY_STEPS[flow];
}

// How many client-side sub-steps signup-password owns for this flow. Both
// flows start that screen at overall step 3, so overall = 2 + subStep.
export function subStepCount(flow: SignupFlow): number {
  return flow === "B" ? 2 : 3;
}
